/* eslint-disable default-case */
import {
  GeneralInfoInProgress,
  InProgress,
  PartItem,
  ShippingTips,
} from '@/features/shipment/types';
import store from '@/features/common/store';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { NotificationData } from '@/features/settings/types';
import { isEnabledPref } from '.';
import { getTotalInsurance } from './createShipment';

const getTotalPartsValue = (customsParts: PartItem[]) => {
  if (!customsParts.length) return 0;

  let value = 0;
  customsParts.forEach((part) => {
    value += (+part.unitValue) * (+part.quantity);
  });

  return value;
};

const getIsPersonalEffect = (
  commercialInfo: GeneralInfoInProgress | undefined,
  shippingTips: ShippingTips | null,
): boolean => {
  if (!commercialInfo) return false;
  if (!shippingTips) return false;

  return commercialInfo?.reasonForExport === 'PersonalEffects'
    && shippingTips.personalEffects.isRequired;
};

const getIsB13a = (
  shippingTips: ShippingTips | null,
  inProgressData: InProgress | null,
  totalPartsValue: number,
): boolean => {
  if (!shippingTips) return false;

  if (inProgressData && inProgressData.fromAddress) {
    const fromCountryData = store.getters.getCountryFromCode(
      inProgressData.fromAddress.countryCode,
    );
    if (fromCountryData && fromCountryData.iataCode === 'CA') {
      if (shippingTips?.iataCountryCode !== 'US'
        && shippingTips?.iataCountryCode !== 'PR'
        && shippingTips?.iataCountryCode !== 'VI'
        && totalPartsValue >= 2000
      ) {
        return true;
      }
    }
  }
  return false;
};

const getIsInsuranceWhenCustoms = (
  inProgressData: InProgress | null,
  totalValue: number,
) => {
  if (!inProgressData) return false;
  if (!inProgressData.packages.length) return false;

  return getTotalInsurance(inProgressData.packages) < 100 && totalValue >= 100;
};

const getIsBattery = (customsParts: PartItem[] | undefined): boolean => {
  if (!customsParts) return false;
  if (!customsParts.length) return false;

  const partWithBatteryTerm = customsParts.find((part) => {
    const description = part.detailedDescription.toLowerCase();

    // * note: In french the spelling is batterie
    if ((description.indexOf('battery') >= 0)
      || (description.indexOf('batterie') >= 0)
      || (description.indexOf('batteries') >= 0)) {
      return true;
    }
    return false;
  });

  return !!partWithBatteryTerm;
};

export const getCustomsNotifications = (
  commercialInfo: GeneralInfoInProgress | undefined,
  shippingTips: ShippingTips | null,
  onSubmit: () => void,
): void => {
  const notificationSettings = store.getters.getNotificationPrefs;
  const inProgressData: InProgress | null = store.getters.getInProgress;
  const notificationData: Array<NotificationData> = [];

  const totalPartsValue = commercialInfo?.customsParts
    ? getTotalPartsValue(commercialInfo?.customsParts)
    : 0;
  const isPersonalEffects = getIsPersonalEffect(commercialInfo, shippingTips);
  const isB13a = getIsB13a(shippingTips, inProgressData, totalPartsValue);
  const isNoInsuranceWhenCustomsValueDeclared = getIsInsuranceWhenCustoms(
    inProgressData, totalPartsValue,
  );
  const isBattery = getIsBattery(commercialInfo?.customsParts);

  if (shippingTips && isPersonalEffects && isEnabledPref(notificationSettings, 'PersonalEffects')) {
    notificationData.push({
      name: 'PersonalEffects',
      extra: {
        formName: shippingTips?.personalEffects.name,
        formLink: shippingTips?.personalEffects.link,
      },
    });
  }

  if (isB13a && isEnabledPref(notificationSettings, 'B13a')) {
    notificationData.push({ name: 'B13a' });
  }

  if (isNoInsuranceWhenCustomsValueDeclared
      && isEnabledPref(notificationSettings, 'NoInsuranceWhenCustomsValueDeclared')
  ) {
    notificationData.push({ name: 'NoInsuranceWhenCustomsValueDeclared' });
  }

  if (isBattery && isEnabledPref(notificationSettings, 'Battery')) {
    notificationData.push({ name: 'Battery' });
  }

  // if (isCurrencyPriceChange && isEnabledPref(notificationSettings, 'CurrencyPriceChange')) {
  //   notificationData.push({ name: 'CurrencyPriceChange' });
  // }

  if (notificationData.length) {
    store.dispatch('showNotification', notificationData);
    eventBus.$once(eventNames.NOTIFICATION_SUBMIT, onSubmit);
  } else {
    onSubmit();
  }
};
